import React from "react";
import { calcCommissionBreakdown, CALC_EXEMPT_LABEL, CALC_EXEMPT_TITLE, CALC_RATE_PLACEHOLDER, CALC_CARD_MAX_WIDTH } from "../lib/commissionCalculator";

// ===== Commissions › Calculator card =====
//
// A standalone scratchpad: no persistence, no API. State lives on
// CommissionsPage (calcUnit / calcRate / calcWithholdingExempt) so it survives
// tab switches, and arrives here as props; the math is calcCommissionBreakdown
// (src/lib/commissionCalculator.js), which a gate runs directly.
//
// Shape (owner ruling 2026-10-31, second pass): the ORIGINAL card — title,
// Unit Value (EGP), Commission Rate (%) stacked below it, the dashed result box
// with Gross Commission (incl. VAT) · Net of VAT · VAT 14% · the checkbox line ·
// Net Due (green, last line) — in its original sizes and styles, with exactly
// two things kept from the re-layout the owner reverted the same day:
//   - the card is CENTRED on the page (max-width 600 as before, margin auto);
//   - the checkbox reads "No 5% W/H" (the Claims badge wording) in Claims' sense:
//     unchecked (default) = withholding applied; checked = 0, Net Due = Gross.
// Dropped: side-by-side inputs, red "−" lines, the big green Net Due box and its
// changing subline. Do not bring them back without a new instruction.
//
// Helpers (FormRow, MoneyInput, fmtMoney, parseMoney, the C palette) come in as
// props rather than being copied: App.js owns them, and the gate hands the real
// ones in.

export default function CommissionCalculatorCard(p) {
  var C = p.C || {};
  var fmt = p.fmtMoney;
  var FormRow = p.FormRow;
  var MoneyInput = p.MoneyInput;
  var unitNum = p.parseMoney(p.unit);
  var b = calcCommissionBreakdown(isFinite(unitNum) ? unitNum : 0, p.rate, p.exempt);
  var text = C.text || "#1E293B";
  var textLight = C.textLight || "#64748B";
  var withholdingColor = p.exempt ? textLight : text;

  var row = function(key, label, value, strong) {
    return <div data-calc-line={key} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
      <span>{label}</span>
      {strong ? <b data-calc-value={key}>{fmt(value)}</b> : <span data-calc-value={key}>{fmt(value)}</span>}
    </div>;
  };

  return <div data-calc-card style={{ maxWidth: CALC_CARD_MAX_WIDTH, margin: "0 auto 18px", background: "#fff", border: "1px solid #E8ECF1", borderRadius: 10, padding: "14px 16px" }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: text, marginBottom: 10 }}>Commission Calculator</div>
    <div data-calc-inputs>
      <FormRow label="Unit Value (EGP)">
        <MoneyInput value={p.unit} onChange={p.onUnit}/>
      </FormRow>
      <FormRow label="Commission Rate (%)">
        <input data-calc-rate type="number" step="0.01" min="0" max="100" value={p.rate} placeholder={CALC_RATE_PLACEHOLDER}
          onChange={function(e){ p.onRate(e.target.value); }}
          style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}/>
      </FormRow>
    </div>
    <div data-calc-breakdown style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 8, padding: "10px 12px", marginTop: 6, fontSize: 12, color: text }}>
      {row("gross", "Gross Commission (incl. VAT)", b.gross, true)}
      {row("netOfVat", "Net of VAT", b.netOfVat)}
      {row("vat", "VAT 14%", b.vat)}
      <div data-calc-line="withholding" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <label data-calc-exempt title={CALC_EXEMPT_TITLE} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
          <input type="checkbox" checked={!!p.exempt} onChange={function(e){ p.onExempt(e.target.checked); }} style={{ cursor: "pointer", margin: 0 }}/>
          <span style={{ color: withholdingColor }}>{CALC_EXEMPT_LABEL}</span>
        </label>
        <span data-calc-value="withholding" style={{ color: withholdingColor }}>{fmt(b.withholding)}</span>
      </div>
      <div data-calc-line="netDue" style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, marginTop: 4, borderTop: "1px solid #CBD5E1", color: C.success || "#22C55E", fontWeight: 700 }}>
        <span>Net Due</span><span data-calc-value="netDue">{fmt(b.netDue)}</span>
      </div>
    </div>
  </div>;
}
